import { EventParams } from './types';

// Claves consideradas PII que NUNCA deben llegar a Clarity/GA4 como params/tags en claro.
const PII_KEYS = new Set([
  'email',
  'userEmail',
  'user_email',
  'name',
  'userName',
  'user_name',
  'username',
  'user',
  'userData',
  'distinct_id',
]);

// Rango de marcas diacríticas combinantes (acentos) — fuente ASCII-pura.
const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

// PascalCase/camelCase/texto-en-español → snake_case válido para GA4
// (`[A-Za-z][A-Za-z0-9_]*`, ≤40, sin acentos ni espacios).
export const toGa4EventName = (input: string): string => {
  if (!input) return 'custom_event';
  let s = input
    .normalize('NFD')
    .replace(DIACRITICS, '') // quita diacríticos
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // límites camelCase
    .replace(/[^a-zA-Z0-9]+/g, '_') // no-alfanumérico → _
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  if (!s) s = 'custom_event';
  if (!/^[a-z]/.test(s)) s = `e_${s}`; // GA4 exige iniciar con letra
  return s.slice(0, 40);
};

// Hash SHA-256 del email (Web Crypto). El hash se cachea a nivel de módulo para
// reusarlo como `user_hash` en los eventos sin recomputar.
let cachedUserHash: string | null = null;

export const hashEmail = async (email?: string | null): Promise<string | null> => {
  if (!email) return null;
  try {
    const normalized = email.trim().toLowerCase();
    const data = new TextEncoder().encode(normalized);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const hash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    cachedUserHash = hash;
    return hash;
  } catch {
    return null;
  }
};

export const getCachedUserHash = (): string | null => cachedUserHash;

// Limpia el hash cacheado. Debe llamarse en logout para evitar que eventos del
// próximo usuario (antes de que su identify resuelva) hereden el hash anterior.
export const resetUserHash = (): void => {
  cachedUserHash = null;
};

// Deja solo params escalares, descarta objetos/arrays y PII, y añade user_hash si existe.
// Respeta el tope de 25 params de GA4.
export const flattenParams = (data?: Record<string, unknown>): EventParams => {
  const out: EventParams = {};
  if (data) {
    for (const [key, value] of Object.entries(data)) {
      if (PII_KEYS.has(key)) continue;
      if (value === null || value === undefined) continue;
      const t = typeof value;
      if (t === 'string' || t === 'number' || t === 'boolean') {
        out[key] = value as string | number | boolean;
      }
    }
  }
  const userHash = getCachedUserHash();
  // Tope GA4: 25 params por evento. Reservamos un slot para user_hash para que no
  // sea descartado por el truncado.
  const scalarEntries = Object.entries(out).slice(0, userHash ? 24 : 25);
  const result: EventParams = Object.fromEntries(scalarEntries);
  if (userHash) result.user_hash = userHash;
  return result;
};
